import React, { useMemo } from "react"
import { Helmet } from "react-helmet-async"

/**
 * ContentSecurityPolicy Component
 * 
 * Dynamically generates CSP meta tag based on environment variables.
 * This allows different API URLs per environment (local, UAT, prod).
 * 
 * Note: For production, Nginx also sends CSP header for additional security.
 * The meta tag provides CSP for development mode (npm start).
 */
const ContentSecurityPolicy = () => {
  const cspContent = useMemo(() => {
    // Get API URLs from environment variables
    const backendUrl = process.env.REACT_APP_BACKEND_URL || ""
    const staticFileUrl = process.env.REACT_APP_STATIC_FILE_URL || ""
    const imageUrl = process.env.REACT_APP_IMAGE_URL || ""
    const frontwebUrl = process.env.REACT_APP_FRONTWEB_URL || ""

    // Extract unique domains from URLs
    const extractDomains = (...urls) => {
      const domains = new Set()
      urls.forEach(url => {
        if (url) {
          try {
            const { origin } = new URL(url)
            domains.add(origin)
          } catch {
            // Invalid URL, skip
          }
        }
      })
      return Array.from(domains).join(" ")
    }

    const apiDomains = extractDomains(backendUrl, staticFileUrl, imageUrl, frontwebUrl)
    
    // Check if running in development mode
    const isDev = process.env.NODE_ENV === "development"
    const currentHostname = typeof window !== 'undefined' ? window.location.hostname : 'localhost'
    const devHostWildcard = isDev ? ` ${currentHostname}:* localhost:* ws://${currentHostname}:* ws://localhost:* wss://${currentHostname}:* wss://localhost:*` : ""

    // Build CSP directives
    const directives = [
      // Default fallback
      "default-src 'self'",

      // JavaScript sources
      // - 'unsafe-inline': Required for CRA inline scripts
      // - 'unsafe-eval': Required for TinyMCE
      `script-src 'self' 'unsafe-inline' 'unsafe-eval' code.jquery.com cdnjs.cloudflare.com${devHostWildcard}`,

      // CSS sources
      // - 'unsafe-inline': Required for CRA inline styles
      "style-src 'self' 'unsafe-inline' unpkg.com fonts.googleapis.com",

      // Font sources (Google Fonts)
      "font-src 'self' fonts.gstatic.com data:",

      // Image sources
      `img-src 'self' data: blob: ${apiDomains}${devHostWildcard}`,

      // AJAX/fetch/WebSocket connections
      `connect-src 'self' ${apiDomains}${devHostWildcard}${!isDev ? " wss://*.fnthai.com" : ""}`,

      // Prevent base tag injection (OWASP requirement)
      "base-uri 'self'",

      // Restrict form submissions
      "form-action 'self'",

      // Block plugins like Flash (OWASP requirement)
      "object-src 'none'",
    ]

    return directives.join("; ")
  }, [])

  return (
    <Helmet>
      <meta httpEquiv="Content-Security-Policy" content={cspContent} />
    </Helmet>
  )
}

export default ContentSecurityPolicy
